import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Remision } from 'types/remisiones';
import { STORE_STYLES } from '../../../assets/data/arrays';

interface ProductosModalProps {
  isOpen: boolean;
  onClose: () => void;
  remision: Remision | null;
}

const ProductosModal: React.FC<ProductosModalProps> = ({ isOpen, onClose, remision }) => {
  if (!isOpen || !remision) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const totalProductos = remision.productos.reduce((sum, producto) => sum + producto.Cantidad, 0);
  const valorTotalProductos = remision.productos.reduce((sum, producto) => sum + (producto.Cantidad * producto.ValorUnitario), 0);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Productos de la Remisión {remision.Numero}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Información de la remisión */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Cliente:</span>
                <p className="text-gray-900">{remision.IdCliente}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Tienda:</span>
                <p className="text-gray-900">
                  {(() => {
                    const store = STORE_STYLES.find(s => s.value === remision.Prefijo);
                    
                    if (!store) {
                      return <span className="text-gray-500">{remision.Prefijo || 'N/A'}</span>;
                    }
                    
                    return <span className={store.color}>{store.shortValue}</span>;
                  })()}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total Productos:</span>
                <p className="text-gray-900">{totalProductos} unidades</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Valor Total:</span>
                <p className="text-gray-900 font-semibold">{formatCurrency(remision.ValorTotal)}</p>
              </div>
            </div>
          </div>

          {/* Tabla de productos */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cantidad
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Unitario
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {remision.productos.map((producto, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono text-sm text-gray-900">
                        {producto.IdProducto}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">
                        {producto.NombreProducto}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {producto.Cantidad}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-900">
                        {formatCurrency(producto.ValorUnitario)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(producto.Cantidad * producto.ValorUnitario)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Resumen */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Total de productos: {totalProductos} unidades
              </span>
              <span className="text-lg font-semibold text-gray-900">
                {formatCurrency(valorTotalProductos)}
              </span>
            </div>
          </div>

          {/* Botón de cerrar */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductosModal;
